/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the list column shell (`.lbar`, `.lsub`, `.lbody`) and collapse affordances.
 * @state-owner navigation store
 * @allowed-imports React; workspace store; shared components and hooks; list bodies
 * @forbidden API client; module internals
 * @public-api WorkspaceShell
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { WorkspaceSectionActions } from '../../app/feature-composition/WorkspaceSectionActions';
import { WorkspaceSectionContent } from '../../app/feature-composition/WorkspaceSectionContent';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { PanelResizer } from '../../shared/components/Resizer';
import { ChevronLeftIcon, ChevronRightIcon, MoreIcon } from '../../shared/components/VispoIcon';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { StudentListBody } from './list/StudentListBody';
import { TeacherListBody } from './list/TeacherListBody';

export interface ListContent {
  readonly title: string;
  readonly subtitle: string;
  readonly body: React.ReactNode;
}

export function useWorkspaceListContent(): ListContent {
  const role = useActiveRole();
  return role === 'teacher' ? TeacherListBody() : StudentListBody();
}

export function ListColumn() {
  const t = useTranslator();
  const collapsed = useWorkspaceUiStore((state) => state.listCollapsed);
  const toggleList = useWorkspaceUiStore((state) => state.toggleList);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const content = useWorkspaceListContent();

  return (
    <>
      <aside className={`list${collapsed ? ' cl' : ''}`} id="list">
        <div className="msheet">
          <div className="mcards" id="mcards" />
          <div className="lbar">
            <b id="ltitle">{content.title}</b>
            <button
              aria-label={t('更多')}
              className="mi"
              title={t('更多')}
              type="button"
              onClick={() => notify(t('更多選項'))}
            >
              <MoreIcon />
            </button>
            <div className="sp" />
            <WorkspaceSectionActions surface="desktop-list" />
            <button
              aria-label={`${t('收合清單')} (⌘\\)`}
              className="cl9"
              title={`${t('收合清單')} (⌘\\)`}
              type="button"
              onClick={toggleList}
            >
              <ChevronLeftIcon />
            </button>
          </div>
          <div className="lsub" id="lsub" style={{ display: content.subtitle ? 'block' : 'none' }}>
            {content.subtitle}
          </div>
          <div className="lbody" id="lbody">
            <WorkspaceSectionContent fallback={content.body} surface="desktop-list" />
          </div>
        </div>
        <PanelResizer max={560} min={260} panel="list" />
      </aside>
      {collapsed ? (
        <button
          className="lstub"
          id="lstub"
          title={t('展開清單')}
          type="button"
          onClick={toggleList}
        >
          <ChevronRightIcon />
        </button>
      ) : null}
    </>
  );
}

export function MobileListColumn({ content }: { content: ListContent }) {
  return (
    <section className="mobile-list-view" aria-labelledby="mobile-workspace-title">
      {content.subtitle ? <p className="mobile-list-view__subtitle">{content.subtitle}</p> : null}
      <div className="mobile-list-view__body">
        <WorkspaceSectionContent fallback={content.body} surface="mobile-list" />
      </div>
    </section>
  );
}
